import { Loader2, PackageCheck } from 'lucide-react';
import { cn, useConfirm, useToast } from '@mustauto/ui/web';
import { formatApiError } from '@mustauto/utils';
import { useDeliverOS } from '@/hooks/useServiceOrders';
import { useCan } from '@/hooks/useCan';

/**
 * "Entregar veículo" — marca a OS concluída como entregue, o par do botão do
 * app. Sem ele o painel não tinha onde registrar a entrega (a OS concluída é
 * só leitura no detalhe), e o carro seguia em "Carros na oficina" depois de ir
 * embora. Só para `completed`: da "aguardando pagamento" a entrega é o fiado,
 * com permissão própria, e segue pelo seletor de status.
 *
 * Sem `os.entregar` não renderiza nada — quem usa o botão põe o `WhoCanHint`
 * no lugar que fizer sentido na tela (abaixo do cabeçalho, do título do grupo).
 */
export function DeliverOSButton({
  osId,
  osNumber,
  plate,
  compact = false,
}: {
  osId: string;
  osNumber: number;
  plate?: string;
  compact?: boolean;
}) {
  const can = useCan();
  const confirm = useConfirm();
  const { toast } = useToast();
  const { deliver, isDelivering } = useDeliverOS(osId);

  if (!can('os.entregar')) return null;

  const label = plate ? `OS #${osNumber} (${plate})` : `OS #${osNumber}`;
  // Mecânico e Recepção entregam mas não reabrem: a confirmação não promete
  // um desfazer que eles não têm.
  const undo = can('os.reabrir')
    ? 'Se foi a OS errada, dá para reabrir em até 48h.'
    : 'Se foi a OS errada, peça ao gestor para reabrir — dá até 48h.';

  const handleClick = async () => {
    const ok = await confirm({
      title: `Entregar o veículo da ${label}?`,
      description: `Marca que o cliente retirou o carro. Ele sai de "Carros na oficina". ${undo}`,
      confirmLabel: 'Entregar veículo',
    });
    if (!ok) return;
    try {
      await deliver();
      toast({ type: 'success', title: `${label} entregue` });
    } catch (err) {
      toast({
        type: 'error',
        title: 'Não foi possível marcar a entrega',
        description: formatApiError(err, 'Tente novamente em instantes.'),
      });
    }
  };

  return (
    <button
      type="button"
      className={cn(
        'flex shrink-0 items-center gap-1.5 rounded-md border border-input font-medium hover:bg-muted disabled:opacity-50',
        compact ? 'h-7 px-2 text-xs' : 'h-8 px-3 text-sm',
      )}
      onClick={() => void handleClick()}
      disabled={isDelivering}
      aria-label={`Entregar veículo da ${label}`}
    >
      {isDelivering ? <Loader2 size={14} className="animate-spin" /> : <PackageCheck size={14} />}
      Entregar veículo
    </button>
  );
}
